import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import NavBar from "../Navbar/Navbar.js";
import Header from '../Header/Header.js';
import './projects.css';
import { useUser } from '../UserContext';
import axiosInstance from '../axiosConfig.js';
import { useTranslation } from "react-i18next";
import { hideLoading, showLoading } from '../store/loadingSlice.js';

const ProjectsPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isNavOpen, setIsNavOpen] = useState(true);
    const { username, companyName } = useUser();
    const licenseData = useSelector((state) => state.liscence.modules);
    const isLoading = useSelector((state) => state.loading.isLoading);

    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const toggleNav = () => setIsNavOpen(!isNavOpen);

    // Filter messages based on search query
    const filteredMessages = messages.filter(msg => 
        msg.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        msg.body.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Function to fetch projects
    const fetchPRJC = async () => {
        dispatch(showLoading());
        const parameters = {
            url: `https://${licenseData.company_url}/s1services`,
            service: "getBrowserInfo",
            clientID: localStorage.getItem("token"),
            appId: licenseData.appid,
            OBJECT: "PRJC",
            LIST: "",
            VERSION: 2,
            LIMIT: 1000,
            FILTERS:"PRJC.ISACTIVE=1"
        };

        try {
            const response = await axiosInstance.post("/api", parameters);
            const rows = response?.data?.rows || [];
            const msgs = rows.map(row => ({
                id: row[0].split(';')[1], // Store clean ID
                title: row[2] || 'No Title',
                body: row[3] || 'No Body'
            }));

            setMessages(msgs);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setMessages([]);
            setError('Failed to load projects');
        } finally {
            dispatch(hideLoading());
        }
    };

    useEffect(() => {
        fetchPRJC();
    }, []);

    const handleCreateProject = async () => {
        if (!title.trim() || !body.trim()) {
            setError('Both title and body are required');
            return;
        }

        setError('');
        dispatch(showLoading());

        try {
            const requestBody = {
                url: `https://${licenseData.company_url}/s1services`,
                service: "setData",
                clientID: localStorage.getItem("token"),
                appId: licenseData.appid,
                OBJECT: "PRJC",
                KEY: "", 
                data: {
                    PRJC:[{
                        CODE: title,
                        NAME: body
                    }]
                },
            };

            const response = await axiosInstance.post("/api", requestBody);
            if (response.data.success) {
                setShowCreateForm(false);
                setTitle('');
                setBody('');
                // Refresh the projects list
                await fetchPRJC();
            } else {
                setError(response.data.message || 'Failed to create project');
            }
        } catch (error) {
            console.error('Error creating project:', error);
            setError(error.response?.data?.message || 'Failed to create project');
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleSaveProject = async () => {
        if (!selectedMessage?.title.trim() || !selectedMessage?.body.trim()) {
            setError('Both code and name are required');
            return;
        }

        setError('');
        dispatch(showLoading());

        try {
            const requestBody = {
                url: `https://${licenseData.company_url}/s1services`,
                service: "setData",
                clientID: localStorage.getItem("token"),
                appId: licenseData.appid,
                OBJECT: "PRJC",
                KEY: selectedMessage.id,
                data: {
                    PRJC: [{
                        CODE: selectedMessage.title,
                        NAME: selectedMessage.body
                    }]
                }
            };

            const response = await axiosInstance.post("/api", requestBody);
            if (response.data.success) {
                // Update local state
                const updatedMessages = messages.map(msg => {
                    if (msg.id === selectedMessage.id) {
                        return selectedMessage;
                    }
                    return msg;
                });

                setMessages(updatedMessages);
                setSelectedMessage(null);
            } else {
                setError(response.data.message || 'Failed to update project');
            }
        } catch (error) {
            console.error('Error updating project:', error);
            setError(error.response?.data?.message || 'Failed to update project');
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleProjectClick = (project) => {
        setSelectedMessage({...project});
    };

    const handleProjectChange = (field, value) => {
        setSelectedMessage(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <div id="module13">
            <div className={`container-card ${isNavOpen ? 'nav-open' : 'nav-closed'}`}>
                <NavBar toggleNav={toggleNav} />
                <div className="dashboard-content">
                    <Header username={username} companyName={companyName} />
                    <div className="main-content">
                        <h2 className="section-title">{t("Projects")}</h2>
                        <div className="projects-search-container">
                            <div className="search-input-container">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" 
                                        stroke="currentColor" strokeWidth="2"/>
                                    <path d="M21 21L16.65 16.65" 
                                        stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                <input
                                    type="text"
                                    placeholder={t("SearchProjects")}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                        </div>
                        <div className="message-list">
                            {filteredMessages.length === 0 ? (
                                <p>{searchQuery ? t("NoProjectsFound") : t("NoProjects")}</p>
                            ) : (
                                filteredMessages.map((msg, index) => (
                                    <div 
                                        key={index} 
                                        className="message-tab" 
                                        onClick={() => handleProjectClick(msg)}
                                    >
                                        <div className="message-title">{msg.title}</div>
                                        <div className="message-body">
                                            {msg.body.length > 30 ? msg.body.substring(0, 30) + "..." : msg.body}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Create Button */}
            <button 
                className="floating-create-btn"
                onClick={() => setShowCreateForm(true)}
                aria-label="Create new project"
            >
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                    <path d="M12 4V20M4 12H20" 
                        stroke="currentColor" strokeWidth="3"/>
                </svg>
            </button>

            {/* Create Project Modal */}
            {showCreateForm && (
                <div className="modal-overlay" onClick={() => !isLoading && setShowCreateForm(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Create New Project</h3>
                        {error && (
                            <div className="error-message">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" 
                                    stroke="#f44336" strokeWidth="2"/>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                        <div className="form-group">
                            <label>Code:</label>
                            <input 
                                type="text" 
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="form-group">
                            <label>Description:</label>
                            <input 
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="form-actions">
                            <button 
                                onClick={() => setShowCreateForm(false)}
                                disabled={isLoading}
                            >
                                Ακύρωση
                            </button>
                            <button 
                                onClick={handleCreateProject}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="loading-spinner"></span>
                                ) : 'Δημιουργία'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Project Modal */}
            {selectedMessage && (
                <div className="modal-overlay" onClick={() => !isLoading && setSelectedMessage(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Επεξεργασία Έργου</h3><br></br>
                        {error && (
                            <div className="error-message">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" 
                                    stroke="#f44336" strokeWidth="2"/>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                        <div className="form-group">
                            <label>Κωδικός:</label>
                            <input
                                type="text"
                                value={selectedMessage.title}
                                onChange={(e) => handleProjectChange('title', e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="form-group">
                            <label>Περιγραφή:</label>
                            <input
                                value={selectedMessage.body}
                                onChange={(e) => handleProjectChange('body', e.target.value)}
                                disabled={isLoading}
                                rows="3"
                            />
                        </div>
                        <div className="form-actions">
                            <button 
                                onClick={() => setSelectedMessage(null)}
                                disabled={isLoading}
                            >
                                Ακύρωση
                            </button>
                            <button 
                                onClick={handleSaveProject}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="loading-spinner"></span>
                                ) : 'Αποθήκευση'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsPage;